/**
 * Shared search matching for the searchable dropdown and filter lists.
 *
 * Long option labels ("Lincoln Property Company") mean a plain substring test
 * misses queries that are correct but out of order or non-adjacent, so each
 * whitespace-separated term is matched on its own.
 */
export function matchesSearchTerms(haystack: string, query: string): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;
  const normalizedHaystack = haystack.toLowerCase();
  return normalizedQuery
    .split(/\s+/)
    .every((term) => normalizedHaystack.includes(term));
}

function isWordChar(char: string | undefined): boolean {
  return char !== undefined && /[a-z0-9]/i.test(char);
}

/**
 * Offsets where a new word begins: the string start, any word character
 * following a separator, and the capital in "BridgeWell".
 */
function wordStartOffsets(haystack: string): number[] {
  const offsets: number[] = [];
  for (let index = 0; index < haystack.length; index++) {
    const char = haystack[index];
    if (!isWordChar(char)) continue;
    const previous = haystack[index - 1];
    const startsWord = index === 0 || !isWordChar(previous);
    const startsCapital =
      char === char.toUpperCase() &&
      char !== char.toLowerCase() &&
      previous === previous?.toLowerCase();
    if (startsWord || startsCapital) offsets.push(index);
  }
  return offsets;
}

/**
 * Stricter form of {@link matchesSearchTerms}: every term must begin a word.
 *
 * Party names repeat common fragments, so a plain substring test buries the
 * wanted row — "us bank" otherwise matches "Fl-us-hing Bank" and "Tr-us-tee".
 */
export function matchesSearchTermsAtWordStart(
  haystack: string,
  query: string,
): boolean {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return true;
  const normalizedHaystack = haystack.toLowerCase();
  const offsets = wordStartOffsets(haystack);
  return normalizedQuery
    .split(/\s+/)
    .every((term) =>
      offsets.some((offset) => normalizedHaystack.startsWith(term, offset)),
    );
}

/**
 * Filters by word-start matching, falling back to substring matching when that
 * finds nothing, so a mid-word query ("tibank") still reaches its option.
 */
export function filterBySearchTerms<T>(
  items: readonly T[],
  query: string,
  haystackOf: (item: T) => string,
): T[] {
  if (!query.trim()) return [...items];
  const wordStartMatches = items.filter((item) =>
    matchesSearchTermsAtWordStart(haystackOf(item), query),
  );
  if (wordStartMatches.length > 0) return wordStartMatches;
  return items.filter((item) => matchesSearchTerms(haystackOf(item), query));
}
